import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { ROUTE_LOGIN, ROUTE_PLAY_NOW_BANKID, ROUTE_PLAY_NOW_DGA } from '~/components/router/constants.js';
import {
	showBankidLogin,
	showUsernameAndPasswordLogin,
	setPostAuthLocation,
	updateCredentials,
	getPreloginStatus,
	handleLoginResult,
	login,
	showPhoneLogin
} from '../ducks/index.js';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer.js';
import useCaptcha from '../recaptcha/use-captcha.js';
import useUIContext from '~/ui/use-ui-context.ts';
import { STATUS as CAPTCHA_STATUS } from '../recaptcha/constants.js';
/**
 * Initialize a login flow
 *
 * @example
 * import  useFullLogin from `@comeon/authentication`;
...
 * const { redirectToLogin, getLoginRoute } = useFullLogin();
 * redirectToLogin({from});
 * redirects the user to login page
 * from is an optional parameter that can be passed if user needs to be redirected
 * to a url after login success
 * getLoginRoute();
 * returns the correct login route
 * ...
 *
 */
const useFullLogin = (loadCaptcha = false) => {
	const navigate = useNavigate();
	const { oneBankIDFlowEnabled, oneButtonFlowEnabled, bankIdEnabled, enableNorwayExpressLogin } = useSelector(
		state => state.brand,
		shallowEqual
	);
	const dispatch = useDispatch();
	const transformPath = useContextualPathTransformer();
	const redirectToLogin = useCallback(
		({ options, from } = {}) => {
			const params = from ? { from } : {};

			if (from) {
				dispatch(setPostAuthLocation(from));
			}

			if (oneBankIDFlowEnabled || oneButtonFlowEnabled) {
				const route = oneButtonFlowEnabled ? ROUTE_PLAY_NOW_DGA : ROUTE_PLAY_NOW_BANKID;
				const state = { ...params };
				navigate(transformPath(route, options), {
					state
				});
			} else {
				const state = {
					...params,
					selectedTab: enableNorwayExpressLogin ? 'phone-login' : 'login',
					loginReferrer: 'product'
				};
				navigate(transformPath(ROUTE_LOGIN, options), { state });
				if (enableNorwayExpressLogin) {
					dispatch(showPhoneLogin());
				} else if (!bankIdEnabled && !enableNorwayExpressLogin) {
					dispatch(showUsernameAndPasswordLogin());
				} else {
					dispatch(showBankidLogin());
				}
			}
		},
		[
			bankIdEnabled,
			dispatch,
			enableNorwayExpressLogin,
			navigate,
			oneBankIDFlowEnabled,
			oneButtonFlowEnabled,
			transformPath
		]
	);

	const cachedEmail = useSelector(state => state.authentication)?.email ?? '';
	const uiContext = useUIContext();
	// loadCaptcha should only be true when handle login is being used from this hook.
	// Otherwise captcha is loaded unnecessarily and it causes perf issues
	const { captchaToken, resolveCaptcha, captchaStatus } = useCaptcha(loadCaptcha);
	const [captchaLogin, setCaptchaLogin] = useState(false);

	const getLoginRoute = useCallback(() => {
		if (oneBankIDFlowEnabled) {
			return ROUTE_PLAY_NOW_BANKID;
		}

		if (oneButtonFlowEnabled) {
			return ROUTE_PLAY_NOW_DGA;
		}

		return ROUTE_LOGIN;
	}, [oneBankIDFlowEnabled, oneButtonFlowEnabled]);

	// run the effect after captcha is resolve to finish verification
	useEffect(() => {
		if (captchaToken && captchaLogin) {
			dispatch(login(uiContext));
			setCaptchaLogin(false);
		}
	}, [captchaToken, uiContext, dispatch, captchaLogin]);

	const handleLogin = async payload => {
		// set email and password in redux state
		if (payload) {
			dispatch(updateCredentials(payload));
		}
		// get email from payload or redux store
		const email = payload?.email ?? cachedEmail;
		const preloginResponse = await getPreloginStatus(email);
		let triggerCaptcha = false;
		if (preloginResponse?.status === 'SUCCESS') {
			triggerCaptcha = preloginResponse?.result;
			resolveCaptcha({
				triggerCaptcha
			});
			if (triggerCaptcha) {
				setCaptchaLogin(true);
			}
		} else {
			return handleLoginResult(preloginResponse, dispatch, uiContext);
		}
		if (!triggerCaptcha) {
			return dispatch(login(uiContext));
		}
	};

	return {
		redirectToLogin,
		getLoginRoute,
		handleLogin,
		captchaInProgress: captchaStatus === CAPTCHA_STATUS.IN_PROGRESS
	};
};

export default useFullLogin;
